import { useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { auth, sites as sitesApi, stats as statsApi, type ModelId } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, PageLoading } from '../components/AuthLayout'
import { MentionTrendChart, MentionBreakdownChart } from '../components/MentionCharts'
import { MultiSelect } from '../components/MultiSelect'
import { DateRangePicker } from '../components/DateRangePicker'

const MODEL_LABELS: Record<ModelId, string> = { chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini' }
const MODEL_OPTIONS = Object.entries(MODEL_LABELS).map(([value, label]) => ({ value, label }))
const LANGUAGE_OPTIONS = [
  { value: 'nl', label: 'Nederlands' },
  { value: 'en', label: 'Engels' },
]

// Reads/writes a comma-separated filter value from the URL's search params — the whole
// point is that reloading, sharing, or bookmarking the dashboard with filters applied
// lands back on the same filtered view, not the unfiltered default.
function paramList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key)
  return raw ? raw.split(',').filter(Boolean) : []
}


export function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()
  const siteList = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list, enabled: me.isSuccess })


  const [searchParams, setSearchParams] = useSearchParams()
  const selectedModels = paramList(searchParams, 'models')
  const selectedSites = paramList(searchParams, 'sites')
  const selectedLanguages = paramList(searchParams, 'languages')
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  function setFilter(key: string, values: string[]) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (values.length === 0) next.delete(key)
        else next.set(key, values.join(','))
        return next
      },
      { replace: true },
    )
  }
  function setRange(nextFrom: string, nextTo: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        nextFrom ? next.set('from', nextFrom) : next.delete('from')
        nextTo ? next.set('to', nextTo) : next.delete('to')
        return next
      },
      { replace: true },
    )
  }

  const siteOptions = (siteList.data ?? []).map((s) => ({ value: String(s.id), label: s.name }))

  const mentionStats = useQuery({
    queryKey: ['mention-stats', 'global', selectedModels, selectedSites, selectedLanguages, from, to],
    queryFn: () =>
      statsApi.mentions(undefined, {
        models: selectedModels as ModelId[],
        siteIds: selectedSites.map(Number),
        languages: selectedLanguages,
        from,
        to,
      }),
    enabled: me.isSuccess,
  })

  async function onLogout() {
    await auth.logout()
    queryClient.setQueryData(['me'], undefined)
    navigate('/login')
  }

  const notLoggedIn = !me.isLoading && (me.isError || !me.data)

  // Redirecting has to happen in an effect, not inline in the render body: calling
  // navigate() directly while rendering Dashboard updates the router (a different
  // component) mid-render, which React warns about ("Cannot update a component while
  // rendering a different component") and which double-fires under StrictMode's
  // double-render.
  useEffect(() => {
    if (notLoggedIn) navigate('/login')
  }, [notLoggedIn, navigate])

  if (me.isLoading) return <PageLoading />
  if (me.isError || !me.data) return null

  return (
    <AuthLayout title={me.data.org_name} wide>
      <p className="-mt-4 mb-6 text-sm">
        <Link to="/sites" className="text-amber">
          Sites beheren →
        </Link>
      </p>

      <div className="mb-4 rounded-xl border border-line bg-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-ink-soft">
            {from && to ? `Vermeldingen (${from} t/m ${to})` : 'Vermeldingen (laatste 30 dagen)'}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <MultiSelect label="Modellen" options={MODEL_OPTIONS} selected={selectedModels} onChange={(v) => setFilter('models', v)} />
            <MultiSelect label="Sites" options={siteOptions} selected={selectedSites} onChange={(v) => setFilter('sites', v)} />
            <MultiSelect label="Talen" options={LANGUAGE_OPTIONS} selected={selectedLanguages} onChange={(v) => setFilter('languages', v)} />
            <DateRangePicker from={from} to={to} onChange={setRange} />
          </div>
        </div>
        {mentionStats.isLoading ? (
          <p className="text-ink-soft">Laden…</p>
        ) : !mentionStats.data || mentionStats.data.daily.every((d) => d.total === 0) ? (
          <p className="text-ink-soft">
            {selectedModels.length || selectedSites.length || selectedLanguages.length
              ? 'Geen metingen voor deze filters.'
              : 'Nog geen metingen. Start je eerste meting via een site.'}
          </p>
        ) : (
          <>
            {/* Per-model comparison lives IN the trend chart now (one line per model,
                click a legend entry to toggle it) — a separate "Per model" bar chart next
                to a model dropdown was two different controls for the same one thing. */}
            <MentionTrendChart daily={mentionStats.data.daily} />
            {mentionStats.data.by_site && mentionStats.data.by_site.length > 0 && (
              <div className="mt-4 border-t border-line pt-4">
                <div className="mb-2 text-sm text-ink-soft">Per site</div>
                <MentionBreakdownChart rows={mentionStats.data.by_site} />
              </div>
            )}
          </>
        )}
      </div>

      {!me.data.totp_enabled && (
        <p className="mb-4 text-sm text-ink-soft">
          <Link to="/totp/setup" className="text-amber">
            Tweestapsverificatie inschakelen
          </Link>
        </p>
      )}

      <button
        onClick={onLogout}
        className="w-full rounded-lg border border-line px-4 py-2.5 text-ink-soft hover:bg-card"
      >
        Uitloggen
      </button>
    </AuthLayout>
  )
}
